import { ofType, StateObservable} from "redux-observable";
import { Observable, of } from 'rxjs';
import {ajax} from 'rxjs/ajax';
import {catchError, concatMap, mergeMap, map, debounceTime} from 'rxjs/operators';
import {
    UserViewSettings,
    UserViewSettingsProp,
} from "../../models/common/ViewSettings";
import {ErrorAction, ReduxAction} from "../../models/common/ActionTypes";
import {APP_CONFIG} from "../../config/GeneralConfig";
import {getUrlParamsFromViewSettings} from "../../config/RouteConfig";
import {AppState} from "../../reducers/RootReducer";
import {eventNotificationShow} from "./EventNotificationActions";
import {EVENT_NOTIFICATION_TYPE} from "../../helpers/EventNotificationType";
import {createErrorResponse} from "../../config/languages/definitions/AjaxResponses";
import {ApiReturnType, ApiRouteableProp} from "../../models/common/ApiReturnType";
import {OptionType} from "../../components/form/SearchableSelect";

/**
 * Async select component redux fetch actions
 */
export interface SelectIdentifiable {
    selectIdentifier: string
}
export interface FetchInitialSelectOptionsAction extends ReduxAction<typeof FETCH_INITIAL_SELECT_OPTIONS>, SelectIdentifiable, UserViewSettingsProp, ApiRouteableProp {}

export interface FetchSelectOptionsAction extends ReduxAction<typeof FETCH_SELECT_OPTIONS>, SelectIdentifiable, UserViewSettingsProp, ApiRouteableProp {}

export interface FetchSelectOptionsSuccessAction extends ReduxAction<typeof FETCH_SELECT_OPTIONS_SUCCESS>, UserViewSettingsProp, SelectIdentifiable {
    payload: ApiReturnType< OptionType[] >
}
export interface FetchSelectOptionsErrorAction extends ErrorAction<typeof FETCH_SELECT_OPTIONS_ERROR>, SelectIdentifiable, UserViewSettingsProp {}

export type FetchSelectOptionTypes = FetchSelectOptionsAction | FetchSelectOptionsSuccessAction | FetchSelectOptionsErrorAction | FetchInitialSelectOptionsAction

export const FETCH_SELECT_OPTIONS = "FETCH_SELECT_OPTIONS";
export const FETCH_SELECT_OPTIONS_SUCCESS = "FETCH_SELECT_OPTIONS_SUCCESS";
export const FETCH_SELECT_OPTIONS_ERROR = "FETCH_SELECT_OPTIONS_ERROR";
export const FETCH_INITIAL_SELECT_OPTIONS = "FETCH_INITIAL_SELECT_OPTIONS";

export function fetchInitialSelectOptions( selectIdentifier: string, route: string, userViewSettings: UserViewSettings ): FetchInitialSelectOptionsAction {
    return { type: FETCH_INITIAL_SELECT_OPTIONS, route, selectIdentifier, userViewSettings };
}
export function fetchSelectOptions( selectIdentifier: string, route: string, userViewSettings: UserViewSettings ): FetchSelectOptionsAction {
    return { type: FETCH_SELECT_OPTIONS, route, selectIdentifier, userViewSettings };
}
export function fetchSelectOptionsSuccess<T extends OptionType = OptionType>( selectIdentifier: string, payload: ApiReturnType< T[] >, userViewSettings: UserViewSettings ): FetchSelectOptionsSuccessAction {
    return { type: FETCH_SELECT_OPTIONS_SUCCESS, userViewSettings, selectIdentifier, payload };
}
export function fetchSelectOptionsError(
    selectIdentifier: string,
    error: Error,
    userViewSettings: UserViewSettings
): FetchSelectOptionsErrorAction {
    return {
        type: FETCH_SELECT_OPTIONS_ERROR,
        error,
        userViewSettings,
        selectIdentifier
    };
}

function fetchBlock( action: FetchSelectOptionsAction | FetchInitialSelectOptionsAction, store$: StateObservable<AppState> ) {
    const urlParams = getUrlParamsFromViewSettings(action.userViewSettings);
    const route = (action.route.slice(-1) === "/") ? APP_CONFIG.baseWeb + action.route + urlParams : APP_CONFIG.baseWeb + action.route + "/" + urlParams;
    return ajax( APP_CONFIG.getRequestObject( route) ).pipe(
        map(response => fetchSelectOptionsSuccess(action.selectIdentifier, response.response as any, action.userViewSettings)),
        catchError(error => of(
            fetchSelectOptionsError(action.selectIdentifier, error, action.userViewSettings),
            eventNotificationShow(EVENT_NOTIFICATION_TYPE.customAjaxError,
                createErrorResponse(
                    store$.value.LanguageReducer.current,
                    error.status,
                    action.userViewSettings.viewIdentifier.rawValue,
                    "fetchSelectOptions: " + action.selectIdentifier))
        ))
    );
}

export const fetchInitialSelectOptionsEpic = (
    action$: Observable< FetchInitialSelectOptionsAction >,
    store$: StateObservable<AppState>
) => action$.pipe(
    ofType( FETCH_INITIAL_SELECT_OPTIONS ),
    mergeMap(action => fetchBlock( action, store$ ) )
);

export const fetchSelectOptionsEpic = (
    action$: Observable< FetchSelectOptionsAction >,
    store$: StateObservable<AppState>
) => action$.pipe(
        ofType(FETCH_SELECT_OPTIONS),
        //debounceTime(500),
        mergeMap(action => fetchBlock( action, store$ ))
    );
